import { LIMITS } from '@wiztime/core';
import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import popover from '../../../ui/AddPopover.module.css';
import { Button, ButtonVariant } from '../../../ui/Button';
import { checkProjectName, NameIssue } from '../../settings/settingsValidation';
import { isDuplicateProjectName } from './AddProjectForm';

interface ProjectNameFormProps {
  /** Text the field starts with: the current name of the project being renamed. */
  readonly initialName: string;
  /** Names of the other projects (deactivated ones too) for the duplicate check. */
  readonly otherNames: readonly string[];
  readonly submitLabel: string;
  readonly testIdPrefix: string;
  /** Write the name; resolves to null on success (the popover closes) or the reason it failed (it stays open). */
  readonly onSubmit: (name: string) => Promise<string | null>;
  readonly onDone: () => void;
}

/**
 * Name-only form of the quick-mark tiles' "rename" popover (design 2026-10-15 §2; adding a
 * child goes through the "+" popover's parent select). The checks are the ones of the settings edit form (`checkProjectName`,
 * duplicate names); Enter or the button saves, Esc is handled by the popover. The initial text
 * is selected on first focus so typing replaces it.
 */
export function ProjectNameForm({
  initialName,
  otherNames,
  submitLabel,
  testIdPrefix,
  onSubmit,
  onDone,
}: ProjectNameFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const selected = useRef(false);

  const check = checkProjectName(name);
  const error = !check.ok
    ? check.issue === NameIssue.Empty
      ? t('settings.validation.nameEmpty')
      : t('settings.validation.nameTooLong', { max: LIMITS.projectNameMax })
    : isDuplicateProjectName(check.value, otherNames)
      ? t('newProject.duplicate')
      : null;
  const shown = submitted ? (error ?? saveError) : null;

  const submit = async (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    if (busy) return;
    setSubmitted(true);
    setSaveError(null);
    if (error !== null || !check.ok) return;
    setBusy(true);
    try {
      const failure = await onSubmit(check.value);
      if (failure === null) onDone();
      else setSaveError(failure);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className={popover.form} noValidate onSubmit={(event) => void submit(event)}>
      <label className={popover.field}>
        <span className={popover.label}>{t('newProject.namePlaceholder')}</span>
        <input
          className={popover.input}
          value={name}
          maxLength={LIMITS.projectNameMax}
          aria-invalid={shown !== null}
          autoComplete="off"
          data-testid={`${testIdPrefix}-name`}
          onFocus={(event) => {
            if (selected.current) return;
            selected.current = true;
            event.currentTarget.select();
          }}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            void submit();
          }}
        />
      </label>
      {shown === null ? null : (
        <p className={popover.error} role="alert" data-testid={`${testIdPrefix}-error`}>
          {shown}
        </p>
      )}
      <div className={popover.actions}>
        <Button type="submit" variant={ButtonVariant.Primary} disabled={busy} data-testid={`${testIdPrefix}-submit`}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
